"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ONBOARDING_EVENT_TYPES = exports.PENDING_ONBOARDING_STATE = exports.OnboardingStateSchema = exports.ONBOARDING_STATES = void 0;
exports.resolveOnboardingState = resolveOnboardingState;
exports.nextOnboardingState = nextOnboardingState;
exports.canAccessDuringOnboarding = canAccessDuringOnboarding;
const zod_1 = require("zod");
/**
 * ONBOARDING STATE MACHINE — held on `tenant.onboardingState`, so gating and
 * resumption are the same question. See design §5
 * (docs/superpowers/specs/2026-08-22-onboarding-billing-devices-design.md)
 * and CONTROLLER RULINGS in task-C1-brief.md, which is authoritative for the
 * exact state list.
 *
 * Today a tenant signs up and pays immediately. The new shape holds the
 * tenant at /subscription-setup — no payment on signup — walking through
 * plan → locations → devices → packages → payment before the app unlocks.
 *
 * `tenant.onboardingState` is a NEW OPTIONAL field. Every tenant that exists
 * today has no such field and is already live and paying, so ABSENT MUST
 * MEAN 'active' — see resolveOnboardingState. This is the single most
 * important property here: if absent did not mean active, introducing this
 * field would lock every existing customer out of their own app.
 *
 * ---------------------------------------------------------------------------
 * ACCOUNT-FIRST SIGNUP (2026-08-23-account-first-signup-plan.md, Task 1)
 * ---------------------------------------------------------------------------
 * That plan needs "the state that says this tenant has signed up but has not
 * subscribed yet" for a NEW entry point — a public, self-serve signup that
 * creates a pending tenant BEFORE any card is collected (spec §2/§5). See
 * `PENDING_ONBOARDING_STATE` below for why that is `'signed_up'` itself, not
 * a second value beside it, and `TenantSignupFields` for the deal-code field
 * that rides alongside it.
 */
exports.ONBOARDING_STATES = [
    'signed_up',
    'choosing_plan',
    'choosing_locations',
    'choosing_devices',
    'choosing_packages',
    'entering_payment',
    'payment_confirmed',
    'active',
];
exports.OnboardingStateSchema = zod_1.z.enum(exports.ONBOARDING_STATES);
/**
 * THE PENDING STATE (account-first-signup plan, Task 1) — "this tenant has
 * signed up but has not subscribed yet." This is `'signed_up'` itself, the
 * existing first state above, NOT a second value added beside it.
 *
 * That is a deliberate judgement call, not an oversight: `'signed_up'`
 * already means exactly this — created, nothing chosen yet, restricted by
 * `canAccessDuringOnboarding` to the wizard-only routes below — and the
 * SAME `OnboardingWizard` this plan routes a self-signed-up customer into is
 * the one Task C1 already built and wired to resume from whatever state a
 * tenant is holding. Minting a distinct `'pending'` value with identical
 * semantics would (a) force a duplicate, identical entry in the exhaustive
 * `ONBOARDING_TRANSITIONS` map below — `Record<OnboardingState, ...>` makes
 * that a compile error otherwise, not a suggestion — and (b) split one
 * concept ("just signed up") across two spellings that every future reader
 * would have to know are synonyms. Reusing the existing value keeps
 * `resolveOnboardingState`, `nextOnboardingState` and
 * `canAccessDuringOnboarding` working for account-first tenants with zero
 * additional cases: this export exists only so Task 2 (tenants-service) and
 * Task 4 (platform-app's gate) have one obviously-named, type-checked symbol
 * to write and compare against, instead of a bare `'signed_up'` string
 * literal whose meaning as "the signup-pending state" would otherwise live
 * only in this comment.
 *
 * Distinct from `'active'` by construction (`ONBOARDING_STATES[0]` versus
 * the last element) and returned unchanged by `resolveOnboardingState` for
 * any tenant it is actually stored on — only an ABSENT `onboardingState`
 * resolves to `'active'`; see the file header and Property 1 in
 * onboarding.test.ts, which this file does not alter.
 */
exports.PENDING_ONBOARDING_STATE = 'signed_up';
// One event per edge of the linear chain in design §5. Each event is named
// for the action that was just completed, and it is the trigger that moves
// the tenant OUT of the state where that action happens and into the next
// checkpoint.
//
// ONBOARDING_EVENT_TYPES is the single source of truth — OnboardingEvent is
// DERIVED from it below (same pattern ONBOARDING_STATES / OnboardingState
// uses), rather than being a second hand-written list that can drift: an
// event added to one and forgotten in the other would otherwise compile
// fine and silently under-test whichever union member only exists in the
// type. No event carries a payload beyond `type` today, so one
// `{ type: OnboardingEventType }` shape covers all seven without a
// hand-maintained discriminated union.
exports.ONBOARDING_EVENT_TYPES = [
    'onboarding_started',
    'plan_selected',
    'locations_confirmed',
    'devices_confirmed',
    'packages_confirmed',
    'payment_method_added',
    'subscriptions_created',
];
// The single outgoing edge for every state. `Record<OnboardingState, ...>`
// (not `Partial`) is deliberate: it forces a compile error if
// ONBOARDING_STATES ever grows without this map being updated, the same
// exhaustiveness discipline STATUS_RANK uses in deviceOrders.ts.
//
// 'active' has NO outgoing edges — a completed onboarding cannot be
// re-entered (property 3). And 'active' is the ONLY state reachable from
// 'subscriptions_created', and only when fired from 'payment_confirmed' —
// every other (state, event) pair below is absent from this table, which is
// what makes "no state may skip payment" hold structurally rather than by
// convention: there is no edge to fabricate a shortcut from.
const ONBOARDING_TRANSITIONS = {
    signed_up: { onboarding_started: 'choosing_plan' },
    choosing_plan: { plan_selected: 'choosing_locations' },
    choosing_locations: { locations_confirmed: 'choosing_devices' },
    choosing_devices: { devices_confirmed: 'choosing_packages' },
    choosing_packages: { packages_confirmed: 'entering_payment' },
    entering_payment: { payment_method_added: 'payment_confirmed' },
    payment_confirmed: { subscriptions_created: 'active' },
    active: {},
};
/**
 * Absent means active (see file header). Every caller that needs to know
 * "what onboarding state is this tenant effectively in" should go through
 * this, rather than reading `tenant.onboardingState` directly and risking a
 * bare `undefined` leaking into a switch/comparison.
 */
function resolveOnboardingState(current) {
    return current ?? 'active';
}
/**
 * Pure reducer. `current` is `tenant.onboardingState` exactly as stored
 * (which is `undefined` for every tenant that predates this field).
 *
 * An event that does not match the resolved state's single outgoing edge is
 * a no-op, not a jump — the state is returned unchanged. This is what
 * "no state may skip payment" and "a completed onboarding cannot be
 * re-entered" both reduce to: there is exactly one edge out of each
 * non-terminal state, and zero edges out of 'active'.
 *
 * TOTALITY: `current` is typed `OnboardingState | undefined`, but the real
 * boundary is untyped JSON read out of DynamoDB by C2/C3 and cast to this
 * type — a legacy value, an empty string, or a state renamed by a future
 * migration can all arrive here as a non-null string outside
 * ONBOARDING_STATES. `ONBOARDING_TRANSITIONS[resolved]` is only guaranteed
 * to exist for a value TypeScript actually narrowed, so the lookup below is
 * guarded (`?.`) rather than assumed. On a miss it falls through to
 * `resolved` — the unrecognised value, UNCHANGED. Deliberately not "active"
 * (would hand a corrupted row the whole product without payment) and not
 * the first state (would replay a possibly-paying tenant through the
 * wizard): a garbage value is a data-repair situation neither this
 * function nor its caller can resolve by guessing, so it refuses to move.
 * This also makes canAccessDuringOnboarding and this function fail in the
 * same direction on the same bad input — both closed, not one open.
 */
function nextOnboardingState(current, event) {
    const resolved = resolveOnboardingState(current);
    return ONBOARDING_TRANSITIONS[resolved]?.[event.type] ?? resolved;
}
// Routes reachable while onboarding is in progress, per design §5: "Access
// during onboarding. Reuses the existing unsubscribed gate:
// /subscription-setup, settings and locations only." This is NOT a second
// gating mechanism — App.tsx already restricts a tenant whose status isn't
// active/trial/trialing (tenantAccessState() returning 'suspended' for a
// tenant with no live subscription) down to /subscription-setup, /settings*
// and a couple of always-on utility routes. canAccessDuringOnboarding
// expresses that same restriction from the onboarding-state side, with
// /locations added because design §5's ordering constraint requires
// locations to exist before devices can be chosen, and locations are named
// during onboarding, before any subscription — and therefore before
// tenantAccessState() would have anything else to say — exists at all.
const ONBOARDING_ALLOWED_ROUTE_PREFIXES = ['/subscription-setup', '/settings', '/locations'];
function matchesRoutePrefix(route, prefix) {
    return route === prefix || route.startsWith(`${prefix}/`);
}
/**
 * Pure access predicate. `state` is `tenant.onboardingState` exactly as
 * stored — absent resolves to 'active' (property 1), at which point this
 * predicate imposes no restriction at all: every existing tenant is
 * unaffected by this field's introduction, on both the reducer and the
 * gate.
 *
 * `route` is matched as a PATHNAME ONLY — no query string or hash. Pass
 * `location.pathname` (React Router), not `location.href` or `pathname +
 * search`: `/settings?tab=x` does not match the `/settings` allow-list
 * entry, only `/settings` and `/settings/...` do.
 *
 * An unrecognised (non-'active') `state` value — malformed data, same as in
 * nextOnboardingState — is NOT treated as 'active' here either: it falls
 * through to the restricted branch, same as any other in-progress state.
 * This function never indexes on `state`, so it already fails closed on bad
 * input without needing the same guard nextOnboardingState does.
 */
function canAccessDuringOnboarding(route, state) {
    if (resolveOnboardingState(state) === 'active')
        return true;
    return ONBOARDING_ALLOWED_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(route, prefix));
}
//# sourceMappingURL=onboarding.js.map